import React from "react";
import * as ReactDOM from "react-dom/client";
import "./landing-shared.entry.jsx";
const SiteNav = globalThis.SiteNav ?? globalThis.window?.SiteNav;
const SiteFooter = globalThis.SiteFooter ?? globalThis.window?.SiteFooter;
// AI & Automation deep-dive
const { useState, useEffect } = React;

const Icon = {
  arrow: (p) => <svg width="12" height="12" viewBox="0 0 12 12" fill="none" {...p}><path d="M3 6h6m0 0L6 3m3 3L6 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square"/></svg>,
};

function Hero() {
  return (
    <section className="section section-dark pa-hero">
      <div className="dark-grid"></div>
      <div className="dark-glow"></div>
      <div className="container">
        <div className="reveal">
          <span className="pa-eyebrow"><span className="eb-dot"></span>AI &amp; AUTOMATION · <strong>Business plan · hosted MCP + local stdio</strong></span>
          <h1>Your assistant.<br/>Your data.<br/><em>Your perimeter.</em></h1>
          <p className="pa-hero-sub">
            Give supported assistants and hosted agent clients safe, scoped access to your private apps and control-plane tools without exposing them to the public internet. Hosted MCP for remote workflows, local <code>namedn mcp serve</code> for same-machine workflows.
          </p>
          <div className="pa-hero-ctas">
            <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Connect your assistant <Icon.arrow /></span></a>
            <a href="#how" className="btn btn-on-dark btn-lg"><span>See how it works</span></a>
          </div>
          <div className="pa-hero-trust">
            <span><span className="check">✓</span>Supported clients include Claude Code, Cursor, OpenAI, and Anthropic</span>
            <span><span className="check">✓</span>Scoped tokens — never god-mode</span>
            <span><span className="check">✓</span>Hosted MCP or local stdio</span>
            <span><span className="check">✓</span>Every call audit-logged</span>
          </div>
        </div>

        <div className="pa-stage reveal" style={{ animationDelay: '160ms' }}>
          <span className="pa-stage-label">LIVE · assistant call → scoped token → private app</span>
          <span className="pa-stage-corner tl"></span><span className="pa-stage-corner tr"></span>
          <span className="pa-stage-corner bl"></span><span className="pa-stage-corner br"></span>
          <BigStageViz />
        </div>

        <div className="pa-stats">
          {[
            { num: '47', label: 'Active automation keys', desc: 'Across the example workspace' },
            { num: '8.2K', label: 'Calls today', desc: 'All scoped, all logged, none denied' },
            { num: '12', label: 'Connected assistants', desc: 'Claude Code, Cursor, Codex, hosted clients' },
            { num: '0', label: 'Tokens leaked', desc: 'Reveal-once, scoped, instantly revokable' },
          ].map((s, i) => (
            <div key={i} className="pa-stat reveal" style={{ animationDelay: `${i*80}ms` }}>
              <div className="num"><em>{s.num}</em></div>
              <div className="lbl">{s.label}</div>
              <div className="desc">{s.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BigStageViz() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => (t + 1) % 4), 1500);
    return () => clearInterval(id);
  }, []);
  const calls = [
    { t: '15:42:08', name: 'list_apps', verdict: 'OK · scope:apps.read', col: 'var(--success)' },
    { t: '15:42:11', name: 'read_db("orders")', verdict: 'OK · scope:db.read', col: 'var(--success)' },
    { t: '15:42:14', name: 'delete_user(...)', verdict: 'DENIED · out of scope', col: 'var(--accent)' },
    { t: '15:42:17', name: 'audit.export()', verdict: 'OK · scope:audit.read', col: 'var(--success)' },
  ];
  return (
    <svg viewBox="0 0 1200 460" style={{ width: '100%', height: 'auto', display: 'block', position: 'relative', zIndex: 1 }}>
      {Array.from({length: 9}).map((_, r) =>
        Array.from({length: 18}).map((_, c) =>
          <circle key={`${r}-${c}`} cx={40 + c * 65} cy={40 + r * 50} r="0.7" fill="rgba(255,255,255,0.08)" />
        )
      )}

      {/* Assistants (left) */}
      <g transform="translate(120, 230)">
        <text x="0" y="-160" fontFamily="var(--font-mono)" fontSize="10" fill="var(--text-dark-muted)" textAnchor="middle" letterSpacing="2">ASSISTANTS</text>
        {[
          { y: -110, name: 'claude-code', os: 'MCP · stdio' },
          { y: -40, name: 'cursor', os: 'MCP · http' },
          { y: 30, name: 'codex', os: 'MCP · stdio' },
          { y: 100, name: 'openai-responses', os: 'MCP · http' },
        ].map((d, i) => (
          <g key={i}>
            <rect x="-100" y={d.y - 22} width="200" height="44" rx="3" fill="var(--bg-dark-card)" stroke="var(--border-dark)" strokeWidth="1"/>
            <circle cx="-78" cy={d.y} r="4" fill={tick === i ? 'var(--accent)' : 'var(--text-dark-faint)'}/>
            <text x="-65" y={d.y - 4} fontFamily="var(--font-mono)" fontSize="10" fill="var(--text-dark-primary)">{d.name}</text>
            <text x="-65" y={d.y + 10} fontFamily="var(--font-mono)" fontSize="8" fill="var(--text-dark-muted)" letterSpacing="0.5">{d.os}</text>
          </g>
        ))}
      </g>

      {/* Center — token gateway */}
      <g transform="translate(600, 230)">
        <rect x="-180" y="-160" width="360" height="320" fill="none" stroke="var(--accent)" strokeWidth="1" strokeDasharray="4 6" opacity="0.5"/>
        <text x="-170" y="-140" fontFamily="var(--font-mono)" fontSize="10" fill="var(--accent)" letterSpacing="2">NAMED.NETWORK MCP GATEWAY</text>

        <circle cx="0" cy="0" r="50" fill="var(--bg-dark-card)" stroke="var(--accent)" strokeWidth="1.5"/>
        <circle cx="0" cy="0" r="50" fill="var(--accent-fade)"/>
        <circle cx="0" cy="0" r="40" fill="none" stroke="var(--accent)" strokeWidth="0.5" strokeDasharray="2 3"/>
        <text x="0" y="-3" fontFamily="var(--font-mono)" fontSize="10" fill="var(--accent)" textAnchor="middle" letterSpacing="2">SCOPE</text>
        <text x="0" y="11" fontFamily="var(--font-mono)" fontSize="8" fill="var(--accent)" textAnchor="middle" letterSpacing="1">verify · audit · forward</text>

        {/* Recent calls */}
        <g transform="translate(0, 100)">
          {calls.map((q, i) => (
            <g key={i}>
              <rect x="-160" y={i * 22 - 8} width="320" height="18" fill="var(--bg-dark)" stroke="var(--border-dark)" strokeWidth="0.5"/>
              <text x="-148" y={i * 22 + 4} fontFamily="var(--font-mono)" fontSize="8" fill="var(--text-dark-faint)">{q.t}</text>
              <text x="-100" y={i * 22 + 4} fontFamily="var(--font-mono)" fontSize="9" fill="var(--text-dark-secondary)">{q.name}</text>
              <text x="148" y={i * 22 + 4} fontFamily="var(--font-mono)" fontSize="8" fill={q.col} textAnchor="end" letterSpacing="0.6">{q.verdict}</text>
            </g>
          ))}
        </g>
      </g>

      {/* Private apps (right) */}
      <g transform="translate(1080, 230)">
        <text x="0" y="-160" fontFamily="var(--font-mono)" fontSize="10" fill="var(--text-dark-muted)" textAnchor="middle" letterSpacing="2">PRIVATE APPS</text>
        <rect x="-90" y="-120" width="180" height="240" fill="none" stroke="var(--border-dark-strong)" strokeWidth="1"/>
        {[
          { y: -90, name: 'orders-api', allowed: true },
          { y: -50, name: 'analytics-db', allowed: true },
          { y: -10, name: 'audit-log', allowed: true },
          { y: 30, name: 'admin-panel', allowed: false },
          { y: 70, name: 'billing-svc', allowed: false },
        ].map((s, i) => (
          <g key={i}>
            <rect x="-78" y={s.y - 12} width="156" height="22" fill={s.allowed ? 'transparent' : 'var(--accent-fade)'} stroke={s.allowed ? 'var(--border-dark-subtle)' : 'var(--accent)'} strokeWidth="0.5" strokeDasharray={s.allowed ? 'none' : '2 3'}/>
            <text x="-72" y={s.y + 3} fontFamily="var(--font-mono)" fontSize="9" fill={s.allowed ? 'var(--text-dark-secondary)' : 'var(--text-dark-muted)'} textDecoration={s.allowed ? 'none' : 'line-through'}>{s.name}</text>
            <text x="72" y={s.y + 3} fontFamily="var(--font-mono)" fontSize="8" fill={s.allowed ? 'var(--success)' : 'var(--accent)'} textAnchor="end" letterSpacing="0.5">{s.allowed ? '✓' : '⊘'}</text>
          </g>
        ))}
      </g>

      <path d="M220 230 Q 400 230 540 230" className="route-line live" />
      <path d="M660 230 Q 870 230 990 230" className="route-line live" />
    </svg>
  );
}

function ModesSection() {
  return (
    <section className="section section-light">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">Two ways in</span>
          <h2>Hosted MCP or local stdio. Same workspace, different trust boundary.</h2>
          <p>Most teams start with hosted MCP for remote assistant access. Local <code>stdio</code> is the same-machine path for IDE workflows that should stay close to the device running the agent.</p>
        </div>
        <div className="modes">
          <div className="mode">
            <span className="mode-tag">01 · HOSTED MCP</span>
            <h3>Drop-in endpoint for remote assistant access.</h3>
            <p>We host the MCP server. Supported clients connect over HTTPS with scoped Business tokens or user-delegated auth, depending on the workflow. This is the right default for remote agents and shared Workspace access.</p>
            <ul className="mode-points">
              <li><strong>Hosted MCP</strong> — copy the endpoint and auth details from the AI page</li>
              <li>TLS transport plus scoped bearer-token access for Business workflows</li>
              <li>Best for OpenAI Responses, Anthropic Messages, and shared remote tools</li>
              <li>Workspace status now shows real endpoint metadata and transport readiness</li>
            </ul>
            <div className="mode-code">
              <div className="com"># Hosted MCP endpoint</div>
              <div><span className="prompt">$</span> url: <span style={{ color: 'var(--accent)' }}>https://mcp.named.network/mcp</span></div>
              <div><span className="prompt">$</span> auth: Bearer <span style={{ color: 'var(--text-dark-muted)' }}>named_live_•••••</span></div>
            </div>
          </div>
          <div className="mode">
            <span className="mode-tag">02 · LOCAL STDIO</span>
            <h3>One command for same-machine IDE workflows.</h3>
            <p><code>namedn mcp serve</code> runs locally and exposes the workstation MCP bridge over <code>stdio</code>. This is the right path when the assistant and the named.network agent are on the same machine.</p>
            <ul className="mode-points">
              <li><strong>Same-machine workflow</strong> — IDE agent launches the bridge directly</li>
              <li>Approval-aware local expose flow is already validated</li>
              <li>Best current fit for Claude Code, Cursor, and Codex workstation flows</li>
              <li>The dashboard shows a truthful compatibility matrix, including the current Codex caveat</li>
            </ul>
            <div className="mode-code">
              <div className="com"># claude_desktop_config.json</div>
              <div>"command": "namedn",</div>
              <div>"args": ["mcp", "serve"]</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Workflow() {
  return (
    <section className="section section-dark" id="how">
      <div className="container">
        <div className="section-h">
          <span className="section-label">Hosted path</span>
          <h2 style={{ color: 'var(--text-dark-primary)' }}>Four steps to the first hosted MCP call.</h2>
          <p>The local <code>stdio</code> path uses the same AI page, but copies a local command instead of minting a hosted automation token.</p>
        </div>
        <div className="workflow">
          {[
            { num: '01', dur: '~30s', title: 'Create an automation key', body: 'Name it after the assistant or workflow. We generate a token you reveal once.' },
            { num: '02', dur: '~1m', title: 'Pick scope families', body: 'Choose only the hosted MCP scope families this assistant needs: devices, protected apps, setup, Secure DNS, private network, or bounded egress.' },
            { num: '03', dur: '~30s', title: 'Optional: set expiry', body: 'No expiry, 7 days, 30 days, or custom. Rotate any time, revoke instantly.' },
            { num: '04', dur: '~2m', title: 'Paste the hosted endpoint into your client', body: 'Copy the MCP endpoint and auth details into the supported hosted client. The local stdio route stays separate and workstation-specific.' },
          ].map((s, i) => (
            <div key={i} className="wf-step">
              <div className="wf-num"><span>STEP {s.num}</span><span className="dur">{s.dur}</span></div>
              <h4>{s.title}</h4>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 48 }}>
          <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Create my first key <Icon.arrow /></span></a>
        </div>
      </div>
    </section>
  );
}

function Scopes() {
  const scopes = [
    { num: '01', name: 'Devices read', code: 'mcp:devices:read', desc: 'Inspect enrolled devices, status, and current agent state.' },
    { num: '02', name: 'Devices write', code: 'mcp:devices:write', desc: 'Run approval-aware device mutations when the workflow really needs them.' },
    { num: '03', name: 'Protected apps read', code: 'mcp:private_apps:read', desc: 'List and inspect protected apps and their current health state.' },
    { num: '04', name: 'Protected apps write', code: 'mcp:private_apps:write', desc: 'Use approval-aware protected-app mutations instead of broad credentials.' },
    { num: '05', name: 'Setup tokens write', code: 'mcp:setup_tokens:write', desc: 'Create install commands and bootstrap new device enrollment safely.' },
    { num: '06', name: 'Secure DNS read', code: 'mcp:secure_dns:read', desc: 'Inspect Secure DNS profiles, assignments, and diagnostics.' },
    { num: '07', name: 'Private network read', code: 'mcp:private_network:read', desc: 'Read route, peer, and health diagnostics for network troubleshooting.' },
    { num: '08', name: 'Bounded egress write', code: 'mcp:egress:write', desc: 'Create explicit, bounded public-web tasks for research or retrieval workflows.' },
  ];
  return (
    <section className="section section-light-alt">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">Hosted scopes</span>
          <h2>Eight hosted scope families. Pick the smallest set.</h2>
          <p>Hosted MCP keys carry narrow tool-family scopes. The gateway checks every call, denies anything outside scope, and writes the deny to audit. Local <code>stdio</code> does not use these OAuth scopes; it relies on local agent identity plus approval-aware flows.</p>
        </div>
        <div className="scope-grid">
          {scopes.map((s, i) => (
            <div key={i} className="scope-tile">
              <span className="scope-num">{s.num}</span>
              <h4>{s.name}</h4>
              <code>{s.code}</code>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section className="section section-light">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">Why teams ship this</span>
          <h2>Assistant access without the leak risk.</h2>
          <p>The hard part isn't connecting an assistant. It's connecting one without giving it your god-mode database password and hoping nothing prompts injection.</p>
        </div>

        <FeatureRow
          flip={false}
          tag="01 · TOKEN MANAGER"
          title="Reveal once. Revoke cleanly. Recreate when needed."
          body="Hosted Business tokens are shown one time at creation. Lose one? You revoke it and mint a new token with the scopes you still need. The current AI page already treats token lifecycle as a first-class control surface instead of hiding it behind raw secrets."
          bullets={[
            "<strong>Reveal-once</strong> — we never store the raw value",
            "Revoke immediately when a token should stop working",
            "Optional expiry keeps hosted credentials short-lived when you want that boundary",
            "Last-used timestamp and current scopes stay visible per key",
          ]}
          viz={<TokenViz />}
        />

        <FeatureRow
          flip={true}
          tag="02 · AUDIT EVENTS"
          title="Tool-call audit with scope checks and verdicts."
          body="Hosted MCP writes first-class audit events for approval requests, tool calls, and success or failure outcomes. The current Business surface is strongest on explicit readiness, token lifecycle, and audit-backed control rather than deep per-key analytics exports."
          bullets={[
            "Per-call: <strong>tool, scope, verdict, latency</strong>",
            "Approval requests and rejections become first-class audit events",
            "Hosted MCP readiness is based on real metadata and transport probes",
            "Deeper token usage history is still thinner than the rest of the control plane today",
          ]}
          viz={<AuditViz />}
        />

        <FeatureRow
          flip={false}
          tag="03 · APPROVAL FLOWS"
          title="Mutating tools use an approval-aware second call."
          body="High-risk hosted and local mutations do not execute on the first request. Instead, the tool returns an approval-required preview and a signed approval token. The second explicit call is what actually changes live state."
          bullets={[
            "<strong>Approval-required preview</strong> comes before the live mutation",
            "The approval token is bound to the requested action",
            "Read-only hosted diagnostics stay immediate; risky writes stay explicit",
            "The same approval-aware pattern already exists across current MCP mutations",
          ]}
          viz={<ApprovalViz />}
        />

        <FeatureRow
          flip={true}
          tag="04 · COST &amp; RATE LIMITS"
          title="The current Business surface stays bounded and honest."
          body="named.network already keeps hosted MCP scope families narrow and high-risk network actions bounded. The AI page is explicit about what exists today: Business entitlement, hosted readiness, local compatibility, token lifecycle, and approval-aware mutations."
          bullets={[
            "<strong>mcp:egress:write</strong> stays separate from read-only diagnostics",
            "Recently used keys and last-used timestamps are already visible",
            "Hosted and local readiness are explicit instead of guessed from failures",
            "The page stays honest when deeper per-key usage controls are not there yet",
          ]}
          viz={<RateViz />}
        />
      </div>
    </section>
  );
}

function FeatureRow({ flip, tag, title, body, bullets, viz }) {
  return (
    <div className={`feature-row ${flip ? 'flip' : ''}`}>
      <div className="fr-copy reveal">
        <span className="section-label on-light">{tag}</span>
        <h3 dangerouslySetInnerHTML={{ __html: title }} />
        <p>{body}</p>
        <ul className="fr-bullets">
          {bullets.map((b, i) => <li key={i} dangerouslySetInnerHTML={{ __html: b }} />)}
        </ul>
      </div>
      <div className="reveal" style={{ animationDelay: '120ms' }}>{viz}</div>
    </div>
  );
}

function TokenViz() {
  return (
    <div className="viz-frame" data-label="AUTOMATION KEYS · workspace acme-co">
      <span className="viz-corner tl"></span><span className="viz-corner tr"></span>
      <span className="viz-corner bl"></span><span className="viz-corner br"></span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[
          { name: 'cursor-readonly', scopes: 'mcp:private_apps:read · mcp:secure_dns:read', last: '4s ago', calls: '2,481', state: 'active' },
          { name: 'claude-orders-bot', scopes: 'mcp:private_apps:read · mcp:private_network:read', last: '12s ago', calls: '894', state: 'active' },
          { name: 'codex-ops', scopes: 'mcp:private_apps:write · mcp:devices:read', last: '1m ago', calls: '37', state: 'active' },
          { name: 'old-agent', scopes: 'mcp:private_apps:read', last: '14d ago', calls: '0', state: 'revoked' },
        ].map((k, i) => (
          <div key={i} style={{ padding: '14px 16px', background: 'var(--bg-light-card)', border: '1px solid var(--border-light)', borderRadius: 2, opacity: k.state === 'revoked' ? 0.5 : 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text-light-primary)' }}>{k.name}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, padding: '2px 8px', border: `1px solid ${k.state === 'active' ? 'var(--success)' : 'var(--text-light-muted)'}`, color: k.state === 'active' ? 'var(--success)' : 'var(--text-light-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{k.state}</span>
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-light-secondary)', marginBottom: 6 }}>{k.scopes}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-light-muted)' }}>
              <span>last used · {k.last}</span>
              <span>{k.calls} calls today</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AuditViz() {
  return (
    <div className="viz-frame" data-label="AUDIT · live · key=cursor-readonly" style={{ background: 'var(--bg-dark-alt)' }}>
      <span className="viz-corner tl"></span><span className="viz-corner tr"></span>
      <span className="viz-corner bl"></span><span className="viz-corner br"></span>
      <div style={{ background: 'var(--bg-dark-card)', border: '1px solid var(--border-dark)', borderRadius: 2 }}>
        {[
          { t: '15:42:08', tool: 'private_apps.list', scope: 'mcp:private_apps:read', v: 'OK', ms: '12ms', col: 'var(--success)' },
          { t: '15:42:09', tool: 'private_apps.get', scope: 'mcp:private_apps:read', v: 'OK', ms: '8ms', col: 'var(--success)' },
          { t: '15:42:11', tool: 'private_network.health', scope: 'mcp:private_network:read', v: 'OK', ms: '34ms', col: 'var(--success)' },
          { t: '15:42:14', tool: 'private_apps.disable', scope: 'mcp:private_apps:write', v: 'DENY', ms: '2ms', col: 'var(--accent)' },
          { t: '15:42:15', tool: 'devices.list', scope: 'mcp:devices:read', v: 'OK', ms: '18ms', col: 'var(--success)' },
          { t: '15:42:17', tool: 'secure_dns.analytics', scope: 'mcp:secure_dns:read', v: 'OK', ms: '210ms', col: 'var(--success)' },
          { t: '15:42:19', tool: 'setup_tokens.create', scope: 'mcp:setup_tokens:write', v: 'DENY', ms: '1ms', col: 'var(--accent)' },
          { t: '15:42:21', tool: 'egress.create_session', scope: 'mcp:egress:write', v: 'OK', ms: '6ms', col: 'var(--success)' },
        ].map((q, i, arr) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '50px 1fr 90px 50px 36px', gap: 8, alignItems: 'center', padding: '7px 12px', borderBottom: i < arr.length - 1 ? '1px solid var(--border-dark-subtle)' : 'none', fontFamily: 'var(--font-mono)', fontSize: 10 }}>
            <span style={{ color: 'var(--text-dark-faint)' }}>{q.t}</span>
            <span style={{ color: 'var(--text-dark-secondary)' }}>{q.tool}</span>
            <span style={{ color: 'var(--text-dark-muted)', fontSize: 9 }}>{q.scope}</span>
            <span style={{ color: q.col, fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{q.v}</span>
            <span style={{ color: 'var(--text-dark-faint)', fontSize: 9, textAlign: 'right' }}>{q.ms}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ApprovalViz() {
  return (
    <div className="viz-frame" data-label="APPROVAL REQUESTS · pending">
      <span className="viz-corner tl"></span><span className="viz-corner tr"></span>
      <span className="viz-corner bl"></span><span className="viz-corner br"></span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[
          { tool: 'mcp:private_apps:write · disable_app', who: 'codex-ops', when: '14s ago', args: 'app="staging-api"', urgent: false },
          { tool: 'mcp:setup_tokens:write · create_token', who: 'claude-ops-bot', when: '1m ago', args: 'device="new-laptop"', urgent: true },
          { tool: 'mcp:egress:write · create_session', who: 'research-agent', when: '3m ago', args: 'host="news-api.com"', urgent: false },
        ].map((a, i) => (
          <div key={i} style={{ padding: 14, background: a.urgent ? 'var(--accent-fade)' : 'var(--bg-light-card)', border: a.urgent ? '1px solid var(--accent)' : '1px solid var(--border-light)', borderRadius: 2 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-light-primary)' }}>{a.tool}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-light-muted)' }}>{a.when}</span>
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-light-secondary)', marginBottom: 10 }}>
              <span style={{ color: 'var(--text-light-muted)' }}>by</span> {a.who} · <span style={{ color: 'var(--text-light-muted)' }}>args</span> {a.args}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button style={{ padding: '6px 12px', fontFamily: 'var(--font-mono)', fontSize: 10, background: 'var(--accent)', color: '#fff', border: 'none', textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer', borderRadius: 2 }}>Approve</button>
              <button style={{ padding: '6px 12px', fontFamily: 'var(--font-mono)', fontSize: 10, background: 'transparent', color: 'var(--text-light-secondary)', border: '1px solid var(--border-light)', textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer', borderRadius: 2 }}>Deny</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RateViz() {
  const bars = [4, 6, 5, 8, 12, 14, 18, 22, 19, 16, 24, 28, 32, 30, 27, 33, 38, 42, 36, 40, 45, 41, 38, 44];
  const max = Math.max(...bars);
  return (
    <div className="viz-frame" data-label="USAGE · key=cursor-readonly · last 24h" style={{ background: 'var(--bg-dark-alt)' }}>
      <span className="viz-corner tl"></span><span className="viz-corner tr"></span>
      <span className="viz-corner bl"></span><span className="viz-corner br"></span>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div style={{ background: 'var(--bg-dark-card)', border: '1px solid var(--border-dark)', padding: '12px 14px', borderRadius: 2 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-dark-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>RATE · per min</div>
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: 22, color: 'var(--text-dark-primary)', letterSpacing: '-0.015em' }}>44 / 60</div>
          <div style={{ height: 3, background: 'var(--border-dark)', marginTop: 6, overflow: 'hidden' }}>
            <div style={{ width: '73%', height: '100%', background: 'var(--accent)' }}></div>
          </div>
        </div>
        <div style={{ background: 'var(--bg-dark-card)', border: '1px solid var(--border-dark)', padding: '12px 14px', borderRadius: 2 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-dark-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>DAILY · cap 10K</div>
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: 22, color: 'var(--text-dark-primary)', letterSpacing: '-0.015em' }}>2,481</div>
          <div style={{ height: 3, background: 'var(--border-dark)', marginTop: 6, overflow: 'hidden' }}>
            <div style={{ width: '24%', height: '100%', background: 'var(--success)' }}></div>
          </div>
        </div>
      </div>
      <div style={{ background: 'var(--bg-dark-card)', border: '1px solid var(--border-dark)', padding: 14, borderRadius: 2 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-dark-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>CALLS / HOUR · 24H WINDOW</div>
        <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 70 }}>
          {bars.map((b, i) => (
            <div key={i} style={{ flex: 1, height: `${(b / max) * 100}%`, background: i === bars.length - 1 ? 'var(--accent)' : 'var(--border-dark-strong)' }}></div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MidCTA() {
  return (
    <section className="section section-light" style={{ padding: 0 }}>
      <div className="container">
        <div className="cta-inline">
          <div className="cta-inline-text">
            <h4>Connect your first assistant in five minutes.</h4>
            <p>Available on the Business plan. Hosted MCP, scoped Business tokens, explicit readiness, and approval-aware mutations for supported assistant clients.</p>
          </div>
          <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Create a Business workspace <Icon.arrow /></span></a>
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const faqs = [
    { q: 'What is MCP and why should I care?', a: 'Model Context Protocol is the standard many assistant clients use to discover and call tools. named.network uses MCP for both the hosted Business control-plane surface and the local workstation bridge, so supported clients connect without custom per-app adapters.' },
    { q: "Where do tokens live? Are they stored on your servers?", a: "We store the hash, never the raw value. You see the raw token exactly once at creation. Lose it and the safe path is to revoke it and mint a new token — that's intentional. The hash is what we check at the gateway." },
    { q: 'Can an assistant escape its scope through prompt injection?', a: 'Prompt injection does not bypass the hosted scope check by itself. Scopes are enforced at the gateway before the call reaches the private tool surface, and out-of-scope attempts are denied and written to audit.' },
    { q: 'How is this different from giving the assistant a database password?', a: 'A password is god-mode and forever. A scoped Business token is least-privilege and revokable in seconds. You also keep tool-family boundaries and approval-aware mutations instead of handing one secret direct control over everything.' },
    { q: 'Does the local stdio mode work offline?', a: 'It runs locally, but the current product still expects the local bridge to stay tied to the named.network control plane for approval-aware behavior and audit-backed workflows. When that path is unavailable, the safer answer is to fail closed instead of pretending the scope boundary still exists.' },
    { q: 'Will this work with hosted agents we already use?', a: 'Today we can honestly claim hosted support for OpenAI Responses, Anthropic Messages, Claude Code, and Cursor, with Codex support carrying the documented caveat on the local non-interactive path. For custom runtimes, the canonical surface today is hosted MCP plus scoped Business tokens, not a separate public OpenAPI product.' },
    { q: 'How do I keep high-risk agent actions bounded?', a: 'Use the smallest hosted scope family that fits the job and keep mutating tools behind the approval-aware second-call flow. High-risk network-action tools also stay inside the separate mcp:egress:write family instead of riding on broad read scopes.' },
    { q: 'Can I require approval for specific tools?', a: 'The current MCP mutation model already uses explicit approval-aware flows for risky actions: the first call returns an approval-required preview, and the second call with the signed approval token is what executes the change.' },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section className="section section-light">
      <div className="container-narrow">
        <div className="section-h">
          <span className="section-label on-light">FAQ</span>
          <h2>The questions we get most.</h2>
        </div>
        <div className="faq-list">
          {faqs.map((f, i) => (
            <div key={i} className={`faq-item ${open === i ? 'open' : ''}`}>
              <button className="faq-q" onClick={() => setOpen(open === i ? -1 : i)}>
                <span>{f.q}</span><span className="plus">+</span>
              </button>
              <div className="faq-a">{f.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="section section-dark" style={{ padding: '128px 0' }}>
      <div className="dark-grid"></div>
      <div className="dark-glow"></div>
      <div className="container">
        <div className="cta-final">
          <span className="section-label">Your turn</span>
          <h2>Assistants that<br/>don't go off-leash.</h2>
          <p>Hosted MCP for Business workflows, local <code>stdio</code> for same-machine IDE work, scoped tokens, and approval-aware mutations. Connect your assistant to the private stack without pretending the trust boundaries are all the same.</p>
          <div className="ctas" style={{ marginTop: 36 }}>
            <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Create a Business workspace <Icon.arrow /></span></a>
            <a href="https://app.named.network/login" className="btn btn-on-dark btn-lg"><span>Sign in</span></a>
          </div>
          <div style={{ marginTop: 24, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-dark-faint)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Business plan · Hosted MCP + local stdio
          </div>
        </div>
      </div>
    </section>
  );
}

function App() {
  return (
    <>
      <SiteNav />
      <Hero />
      <ModesSection />
      <Workflow />
      <Scopes />
      <Features />
      <MidCTA />
      <FAQ />
      <FinalCTA />
      <SiteFooter />
    </>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<App />);

